import { TXLogger } from "@tx/framework";
import axios from "axios";
import dayjs from "dayjs";
import deepDiff from "deep-diff";
import _ from "lodash";
import { action, makeAutoObservable, runInAction } from "mobx";
import { createElement } from "react";

import {
	OrderTimeoutdReasons,
	areCookiesValid,
	splitDocumentCookies,
} from "@client/context/config";
import RootContext from "@client/context/RootContext";
import getOrderExtras from "@client/helpers/getOrderExtras";
import { getProcessedItemsData } from "@client/helpers/getProcessedItemsData";
import { getAxiosErrorMessage, sleep } from "@client/helpers/utils";
import { TDateISO } from "@client/interfaces";
import {
	ANIMATION_CONSTANTS,
	Courier,
	Order,
	OrderStatuses,
	Order_MinimalDetails,
	OrdersHistoryStatuses,
	SendOrdersMailParams,
	VendorDetails,
} from "@client/stores";

import { GetVendorAndActiveOrdersParams, Totals } from "./interfaces";
import { computeDates, getProcessedProducts } from "./vendor-store.helpers";
import { OpenVersionMessage } from "./version-message";

export class VendorStore {
	debugLog = (message: string) => {
		if (window.location.search.includes("debug=1")) {
			console.log(
				`[${dayjs().format("HH:mm:ss.SSS")}] VendorStore: ${message}`
			);
		}
	};
	pullingDataInProgress = false;
	pullingOrdersHistoryInProgress = false;

	ordersInProcessing: number[] = [];
	show_previous_day_courier = false;
	@action set_show_previous_day_courier = (value: boolean) =>
		(this.show_previous_day_courier = value);

	previous_day_courier_value = false;
	@action set_previous_day_courier_value = (value: boolean) =>
		(this.previous_day_courier_value = value);

	couriers: Courier[] = [];

	activeOrders: Order[] = [];
	activeOrdersTotals: Totals = { Pending: 0, Picking: 0, Packed: 0 };
	activeOrders_search: Order[] = [];
	activeOrdersTotals_search: Totals = { Pending: 0, Picking: 0, Packed: 0 };

	historyOrders: Order[] = [];
	historyOrdersTotal: number;
	historyPagesTotal: number;
	pastOrders: Order[] = [];

	vendorDetails: VendorDetails | undefined = undefined;
	isStoreOpen = false;

	current_app_version = "";

	private pollingInterval?: number;

	constructor(private rootContext: RootContext) {
		makeAutoObservable(this);
	}

	public initialise = async () => {
		await this.rootContext.printStore.initialise();

		await this.getVendorAndActiveOrders({ isInit: true });

		if (
			!this.vendorDetails?.is_selfshipment &&
			this.vendorDetails?.show_handshake_dialog
		) {
			this.PickupsOperations.shipping_signature.get_shippedOrders();
		}
	};

	@action public startPollingInterval() {
		this.debugLog("start PollingInterval");
		this.stopPollingInterval();
		this.debugLog("start PollingInterval - continue");

		this.pollingInterval = window.setInterval(
			this.getVendorAndActiveOrders,
			Number(process.env.REACT_APP_VENDOR_STORE_POLLING_INTERVAL) * 1000
		);
	}
	@action public stopPollingInterval() {
		this.debugLog("stop PollingInterval");
		if (!this.pollingInterval) {
			return;
		}
		this.debugLog("stop PollingInterval - continue");

		window.clearInterval(this.pollingInterval);
		this.pollingInterval = undefined;
	}

	private findOrder = (orderID: number) =>
		this.rootContext.layoutStore.showSearchResults
			? this.activeOrders_search.find((order) => order.id == orderID)
			: this.activeOrders.find((order) => order.id == orderID);

	@action public setScheduledDate = async (
		orderID: number,
		scheduledDate: TDateISO
	) => {
		const order = this.findOrder(orderID);

		if (!order) {
			return;
		}

		order.dates.scheduled_date = scheduledDate;

		this.setOrderStatus(orderID, order.status);
	};
	@action public setActualDate = async (orderID: number) => {
		const order = this.findOrder(orderID);

		if (!order) {
			return;
		}

		order.dates.actual_date = dayjs().toISOString();
	};

	@action private setOrderInProcessing = (orderID: number) => {
		this.ordersInProcessing.push(orderID);
		this.stopPollingInterval();
	};
	@action private removeOrderFromProcessing = (orderID: number) => {
		this.ordersInProcessing.splice(this.ordersInProcessing.indexOf(orderID), 1);
		this.startPollingInterval();
	};

	@action public setOrderStatus = async (
		orderID: number,
		newStatusName: OrderStatuses,
		cancellation_reason?: string,
		rejectedProducts?: string[],
		numberOfPackages?: number
	) => {
		TXLogger.trace(
			"setOrderStatus()",
			`Setting order status (purchaseId: ${orderID})`,
			{
				purchaseId: orderID,
				extra: {
					newStatusName,
					cancellation_reason,
				},
			}
		);
		this.setOrderInProcessing(orderID);

		const order = this.findOrder(orderID);

		if (!order) {
			this.removeOrderFromProcessing(orderID);
			return;
		}

		// so we need to check if the order status is actually changing
		// before moving it to the next column

		const totals = this.rootContext.layoutStore.showSearchResults
			? this.activeOrdersTotals_search
			: this.activeOrdersTotals;
		if (
			order?.status === OrderStatuses.Pending &&
			newStatusName === OrderStatuses.Picking
		) {
			totals.Pending--;
			totals.Picking++;
		} else if (
			order?.status === OrderStatuses.Picking &&
			newStatusName === OrderStatuses.Packed
		) {
			totals.Picking--;
			totals.Packed++;
		} else if (
			order.status === OrderStatuses.Packed &&
			newStatusName === OrderStatuses.Shipped
		) {
			totals.Packed--;
		}

		try {
			const order_initial = _.cloneDeep(order);

			order.dates.last_update = {
				last_status: order.status,
				OrderExtraDetails: getOrderExtras(this.vendorDetails),
			};
			order.status = newStatusName;

			order.dates = computeDates(
				order,
				this.vendorDetails!,
				cancellation_reason
			);

			if (numberOfPackages) {
				order.nr_of_packages = numberOfPackages;
			}

			if (rejectedProducts?.length) {
				order.products = getProcessedProducts(order.products, rejectedProducts);
			}

			// do not print if store is tx donations
			const is_txDonationsStore = this.vendorDetails?.id == 52061;

			if (order.status == OrderStatuses.Packed && !is_txDonationsStore) {
				// if order will be Packed, open the modal while the label is being generated
				this.rootContext.printStore.openPrintingModal(
					process.env.REACT_APP_TIMEOUT_PRINT_GENERATE_LABEL
				);
			}

			const diffs = deepDiff(order_initial, order);

			try {
				const data = await (
					await this.rootContext.apiService.api(
						"/order/change-status-or-dates",
						{
							order,
							diffs,
						}
					)
				).data;
			} catch (error) {
				TXLogger.error(
					"setOrderStatus()",
					"Error while changing the order status",
					{
						error: { stack: error },
						purchaseId: order.id,
						extra: { order },
					}
				);
			}

			if (order.status == OrderStatuses.Packed && !is_txDonationsStore) {
				// after order is actually Packed, a label should be generated for the order reference

				const reference = order.order_meta.reference || String(order.id);

				this.rootContext.printStore.printShippingLabel(reference);
			}
		} catch (error) {
			TXLogger.error(
				"setOrderStatus()",
				"Error while changing the order status",
				{
					error: { stack: error },
					purchaseId: order.id,
					extra: { order },
				}
			);

			if (axios.isAxiosError(error)) {
				const { message, statusCode } = getAxiosErrorMessage(error) || {};

				this.rootContext.modalStore.showError(
					`There was an error while changing the order (${orderID}) status.`,
					`Error code: ${statusCode}; error message: ${message}`
				);
			}
		}

		this.removeOrderFromProcessing(orderID);
	};
	@action public setBulkOrderStatus = async (
		ordersToChange: {
			id: number;
			newStatusName: OrderStatuses;
			cancellation_reason?: string;
			rejectedProducts?: string[];
			numberOfPackages?: number;
		}[]
	) => {
		TXLogger.trace(
			"setOrderStatus()",
			`Setting bulk order status (purchaseIds: ${ordersToChange
				.map((order) => order.id)
				.join(", ")})`,
			{
				extra: {
					purchaseIds: ordersToChange.map((order) => order.id),
					orders: ordersToChange,
				},
			}
		);

		ordersToChange.forEach((order) => {
			this.ordersInProcessing.push(order.id);
		});

		const orders: Order_MinimalDetails[] = this.activeOrders.filter((order) =>
			ordersToChange.some((orderToChange) => orderToChange.id === order.id)
		);

		try {
			// const orders_initial = _.cloneDeep(ordersToChange);

			orders.forEach((order) => {
				const orderToChange = ordersToChange.find(
					(orderToChange) => orderToChange.id === order.id
				);

				order.dates.last_update = {
					last_status: order.status,
					OrderExtraDetails: getOrderExtras(this.vendorDetails),
				};

				// TODO: remove this once the delivery dates functionality is merged
				// @ts-ignore ignored because the dates functionality does not exist on this branch
				order.dates.actual_date = dayjs().toISOString();

				order.status = orderToChange?.newStatusName;

				order.dates = computeDates(
					order,
					this.vendorDetails!,
					orderToChange.cancellation_reason
				);

				if (orderToChange?.numberOfPackages) {
					order.nr_of_packages = orderToChange.numberOfPackages;
				}

				if (orderToChange?.rejectedProducts?.length) {
					order.products = getProcessedProducts(
						order.products,
						orderToChange.rejectedProducts
					);
				}
			});

			// Transform each order into the magento S2sBulkOrderDetailsInput shape (see
			// the field usefulness table). `status` stays as the name here and is mapped
			// to the numeric code server-side. `details` is always sent but only carries
			// the scheduled_date / actual_date the endpoint persists. `items` /
			// `nr_of_packages` are omitted unless there is a value.
			const payload = orders.map((order) => {
				const hasRejectedProducts = order.products?.some(
					(product) => product.order_details?.qtyCanceled
				);

				return {
					id: order.id,
					status: order.status,
					nr_of_packages: order.nr_of_packages ?? undefined,
					items: hasRejectedProducts
						? getProcessedItemsData(order.products)
						: undefined,
					details: JSON.stringify({
						scheduled_date: order.dates?.scheduled_date,
						actual_date: order.dates?.actual_date,
					}),
				};
			});

			// const diffs = deepDiff(orders_initial, orders);
			const response = await this.rootContext.apiService.api(
				"/order/change-bulk-status-or-dates",
				payload
			);

			if (response.data.result.success) {
				ordersToChange.forEach((order) => {
					this.rootContext.layoutStore.setHasCollectedOrder(order.id);
				});
			} else {
				throw new Error(`Failed to change the order status. Failed to change for ${response.data.result.failed_count} orders.
				Please try again later.`);
			}
		} catch (error) {
			TXLogger.error(
				"setBulkOrderStatus()",
				"Error while changing the order status",
				{
					error: { stack: error },

					extra: { orders },
				}
			);

			ordersToChange.forEach((order) => {
				this.ordersInProcessing.splice(
					this.ordersInProcessing.indexOf(order.id),
					1
				);
			});

			this.getVendorAndActiveOrders();

			const errorMessage = error.message;
			if (axios.isAxiosError(error)) {
				const { message, statusCode } = getAxiosErrorMessage(error) || {};

				this.rootContext.modalStore.showError(
					errorMessage,
					`Error code: ${statusCode}; error message: ${message}`
				);
			} else {
				this.rootContext.modalStore.showError(
					errorMessage,
					`orders: ${ordersToChange.map((order) => order.id).join(", ")}`
				);
			}
		}

		ordersToChange.forEach((order) => {
			this.ordersInProcessing.splice(
				this.ordersInProcessing.indexOf(order.id),
				1
			);
		});
	};

	// sometimes the order polling is not working correctly (infra bug)
	// so we need to count the errors and show a full screen error if the errors are too many
	errorCount_activeOrders = 0;

	@action getVendorAndActiveOrders = async ({
		isInit,
		searchTerm,
	}: GetVendorAndActiveOrdersParams = {}) => {
		this.debugLog(
			`getVendorAndActiveOrders - isInit: ${isInit}, searchTerm: ${searchTerm}`
		);
		if (
			this.rootContext.modalStore.isModalOpen ||
			this.ordersInProcessing.length ||
			this.pullingDataInProgress ||
			!this.rootContext.authStore.loggedIn
		) {
			return;
		}
		this.debugLog(`getVendorAndActiveOrders - continue`);

		const validCookies = areCookiesValid(splitDocumentCookies());
		if (!validCookies) {
			this.rootContext.modalStore.showError(
				"Please login again.",
				"Session expired."
			);

			this.rootContext.authStore.logout();
			return;
		}

		this.stopPollingInterval();
		runInAction(() => {
			this.pullingDataInProgress = true;
		});

		this.rootContext.configStore.loadData();
		this.PickupsOperations.info();

		try {
			const response = await this.rootContext.apiService.api(
				"/vendor/active-orders",
				{
					sort: searchTerm
						? this.rootContext.layoutStore.sort_search
						: this.rootContext.layoutStore.sort,
					search: searchTerm,
				}
			);
			const data = response.data;

			const { orders, vendorDetails, isStoreOpen, current_app_version } = data;
			for (const key in vendorDetails.printer) {
				// override printer settings with environment variables if they are set
				if (process.env[`REACT_APP_PRINTER_${key}`]) {
					vendorDetails.printer[key] = process.env[`REACT_APP_PRINTER_${key}`];
				}
			}

			if (!this.current_app_version) {
				this.current_app_version = current_app_version;
			} else if (this.current_app_version !== current_app_version) {
				OpenVersionMessage();
			}

			runInAction(() => {
				this.vendorDetails = vendorDetails;
				this.isStoreOpen = isStoreOpen;

				if (searchTerm) {
					this.activeOrders_search = orders.orders;
					this.activeOrdersTotals_search = orders.total_orders;
				} else {
					const currentNewOrdersLength = this.activeOrders.filter(
						(order) => order.status === OrderStatuses.Pending
					).length;

					this.activeOrders = orders.orders;
					this.activeOrdersTotals = orders.total_orders;

					if (!isInit) {
						const newOrdersLength = orders.orders.filter(
							(order) => order.status === OrderStatuses.Pending
						).length;

						this.checkIsNewOrder(currentNewOrdersLength, newOrdersLength);
					}
				}
			});

			this.errorCount_activeOrders = 0;
		} catch (error) {
			const errMsg = "Could not get orders and vendor details.";
			const { message, statusCode } = getAxiosErrorMessage(error);

			runInAction(() => {
				this.errorCount_activeOrders++;

				if (this.errorCount_activeOrders > 3) {
					this.rootContext.modalStore.showFullscreenError(
						`${errMsg} \n\n ${message} (HTTP status: ${statusCode})`
					);
				}
			});

			TXLogger.error("getVendorAndActiveOrders", errMsg, {
				error: { stack: error, message, statusCode },
			});
		}

		runInAction(() => {
			if (searchTerm) {
				// if getVendorAndActiveOrders is called with searchTerm, then show the search results
				this.rootContext.layoutStore.setShowSearchResults(true);
			} else if (!this.rootContext.layoutStore.showSearchResults) {
				// if getVendorAndActiveOrders is called without searchTerm
				// and we are not already showing the search results, then hide the search results
				this.rootContext.layoutStore.setShowSearchResults(false);
			}

			this.pullingDataInProgress = false;
		});

		this.startPollingInterval();
	};
	z;
	private checkIsNewOrder = (
		currentNewOrdersLength: number,
		newOrdersLength: number
	) => {
		if (newOrdersLength > currentNewOrdersLength) {
			this.rootContext.layoutStore.setIsNewOrder(true);

			try {
				if (window.webkit?.messageHandlers?.playAudio) {
					window.webkit?.messageHandlers?.playAudio?.postMessage("play");
				} else {
					new Audio(
						"/sound/mixkit-bell-gentle-alarm-notification-940.wav"
					).play();
				}
			} catch (error) {
				TXLogger.error(
					"getVendorAndActiveOrders",
					"Error while playing audio",
					{
						error: { stack: error },
					}
				);
			}
		}
	};

	errorCount_ordersHistory = 0;
	@action getVendorAndOrdersHistory = async (
		currentPage: number,
		timeFrameValue: number,
		selectedStatuses: typeof OrdersHistoryStatuses,
		search: string
	) => {
		const validCookies = areCookiesValid(splitDocumentCookies());
		if (!validCookies) {
			// TODO: show an error message
			this.rootContext.authStore.logout();
			return;
		}

		if (!this.rootContext.authStore.loggedIn) {
			return;
		}

		runInAction(() => {
			this.pullingOrdersHistoryInProgress = true;
		});

		try {
			let pageSize = 250;

			if (typeof window !== "undefined") {
				const customPagesize = window.location.search.split("=")[1];
				if (customPagesize && !isNaN(Number(customPagesize))) {
					pageSize = Number(customPagesize);
				}
			}
			const response = await this.rootContext.apiService.api(
				"/vendor/orders/history",
				{ currentPage, pageSize, timeFrameValue, selectedStatuses, search }
			);

			const data = response.data;

			const {
				orders: { orders, orders_total, pages_total },
				current_app_version,
			} = data;

			if (!this.current_app_version) {
				this.current_app_version = current_app_version;
			} else if (this.current_app_version !== current_app_version) {
				OpenVersionMessage();
			}

			runInAction(() => {
				this.historyOrders = orders;
				this.historyOrdersTotal = orders_total;
				this.historyPagesTotal = pages_total;
			});

			this.errorCount_ordersHistory = 0;
		} catch (error) {
			const errMsg = "Could not get orders and vendor details.";
			const { message, statusCode } = getAxiosErrorMessage(error);

			runInAction(() => {
				this.errorCount_ordersHistory++;

				if (this.errorCount_ordersHistory > 3) {
					this.rootContext.modalStore.showFullscreenError(
						`${errMsg} \n\n ${message} (HTTP status: ${statusCode})`
					);
				}
			});

			TXLogger.error("getVendorAndActiveOrders", errMsg, {
				error: { stack: error, message, statusCode },
			});
		}

		runInAction(() => {
			this.pullingOrdersHistoryInProgress = false;
		});
	};
	@action clearOrdersHistory = () => {
		this.historyOrders = [];
	};

	public orderOperations = {
		acceptOrder: async (orderID: number, rejectedProducts?: string[]) => {
			await this.setOrderStatus(
				orderID,
				OrderStatuses.Picking,
				undefined,
				rejectedProducts
			);
			rejectedProducts?.length && (await this.getVendorAndActiveOrders());
		},
		rejectOrder: async (orderID: Order["id"], cancellation_reason?: string) => {
			await this.setOrderStatus(
				orderID,
				OrderStatuses.Rejected,
				cancellation_reason
			);
		},

		readyForDelivery: async (
			orderID: number,
			rejectedProducts?: string[],
			numberOfPackages?: number
		) => {
			await this.setOrderStatus(
				orderID,
				OrderStatuses.Packed,
				undefined,
				rejectedProducts,
				numberOfPackages
			);
			rejectedProducts?.length && (await this.getVendorAndActiveOrders());
		},
		collectedOrder: async (orderID: number) => {
			this.rootContext.layoutStore.setHasCollectedOrder(orderID);

			await sleep(
				ANIMATION_CONSTANTS.ORDER_DONE_ANIMATION_DURATION +
					ANIMATION_CONSTANTS.ORDER_DONE_ANIMATIoN_DURATION_2
			);

			await this.setOrderStatus(orderID, OrderStatuses.Shipped);
		},
		collectedBulkOrders: async (orders: number[]) => {
			orders.forEach((orderID) => {
				this.rootContext.layoutStore.setHasCollectedOrder(orderID);
			});

			await sleep(
				ANIMATION_CONSTANTS.ORDER_DONE_ANIMATION_DURATION +
					ANIMATION_CONSTANTS.ORDER_DONE_ANIMATIoN_DURATION_2
			);

			await this.setBulkOrderStatus(
				orders.map((id) => ({
					id,
					newStatusName: OrderStatuses.Shipped,
				}))
			);
		},

		timeoutOrder: async (orderID: number) => {
			const order = this.findOrder(orderID);

			if (!order) {
				TXLogger.error("timeoutOrder()", `could not find order ${orderID}`, {
					purchaseId: orderID,
				});

				return;
			}

			const cancellation_reason =
				order.status == OrderStatuses.Pending
					? OrderTimeoutdReasons.PENDING_ORDER_TIMEOUT
					: OrderTimeoutdReasons.PICKING_ORDER_TIMEOUT;
			await this.setOrderStatus(
				orderID,
				OrderStatuses.Timedout,
				cancellation_reason
			);
		},

		sendOrdersMail: async (params: SendOrdersMailParams) => {
			const data = await (
				await this.rootContext.apiService.api("/vendor/orders/send-to-email", {
					vendor_id: this.vendorDetails.id,
					...params,
				})
			).data;
		},
	};

	pickupEnabled = false;
	public PickupsOperations = {
		shipping_signature: {
			show_modal: async () => {
				this.PickupsOperations.shipping_signature.get_shippedOrders();

				const { ShippingSignatureModal } = await import(
					"@client/components/shipping-signature-modal/shipping-signature-modal"
				);

				this.rootContext.modalStore.addModal({
					id: "shipping-signature-modal",
					content: createElement(ShippingSignatureModal),
					showCloseBtn: true,
				});
			},

			shipped_orders: [],

			get_shippedOrders: async () => {
				// const response = {
				// 	data: [
				// 		"033256689",
				// 		"033482915",
				// 		"033761204",
				// 		"033905318",
				// 		"033174692",
				// 		"033638507",
				// 		"033290846",
				// 		"033517903",
				// 		"033864125",
				// 		"033349780",
				// 		"033702491",
				// 		"033918634",
				// 		"033456271",
				// 		"033823590",
				// 		"033140768",
				// 		"033679213",
				// 		"033584902",
				// 	],
				// };
				try {
					const response = await this.rootContext.apiService.api(
						"/vendor/signature/signature-orders"
					);

					this.PickupsOperations.shipping_signature.shipped_orders =
						response.data.order_numbers || [];
				} catch (error) {
					TXLogger.error(
						"PickupsOperations.shipping_signature.get_shippedOrders()",
						"failed to get shipped orders",
						{ error }
					);
				}
			},

			send_signature: async (checkedOrders: string[]) => {
				// console.log("send_signature", { checkedOrders });

				try {
					const response = await this.rootContext.apiService.api(
						"/vendor/signature/signature-submit"
					);
				} catch (error) {
					TXLogger.error(
						"PickupsOperations.shipping_signature.send_signature()",
						"failed to send signature",
						{ error }
					);
				}
			},
		},

		previous_day_courier: async (answer: "yes" | "no") => {
			try {
				const response = await this.rootContext.apiService.api(
					"/vendor/pickups/previous_day_courier",
					{ answer }
				);

				return response.data;
			} catch (error) {
				TXLogger.error(
					"PickupsOperations.previous_day_courier()",
					"failed to set previous day courier",
					{ error }
				);
			}

			// automatically show the shipping signature modal when this one is closed
			if (
				!this.vendorDetails?.is_selfshipment &&
				this.vendorDetails?.show_handshake_dialog
			) {
				this.PickupsOperations.shipping_signature.show_modal();
			}

			return false;
		},

		compareDate: (dateISO: string) => {
			runInAction(() => {
				const today = dayjs();
				let wm = dayjs(dateISO).add(1, "day");
				wm = dayjs(`${wm.year()}-${wm.month() + 1}-${wm.date()}`);

				const enabled = today > wm;

				// console.log(
				// 	`today: ${today.toISOString()} \nwm: ${wm.toISOString()} \n`,
				// 	enabled
				// );

				this.pickupEnabled = enabled;
			});
		},

		info: async () => {
			try {
				const response = await this.rootContext.apiService.api(
					"/vendor/pickups/info"
				);

				if (response.data) {
					runInAction(() => {
						// compare previous value with current value
						// if they are all true then set previous_day_courier to false to not show the modal again
						this.show_previous_day_courier = !(
							this.previous_day_courier_value &&
							!!response.data.previous_day_courier
						)
							? !!response.data.previous_day_courier
							: false;
						this.previous_day_courier_value =
							!!response.data.previous_day_courier;
					});

					const data = response.data.packages;
					this.PickupsOperations.compareDate(data.timestamp_iso);
				} else {
					throw "Info came empty";
				}
			} catch (error) {
				TXLogger.error("PickupsOperations.info()", "failed to get info", {
					error,
					extra: { vendorDetails: this.vendorDetails },
				});
			}
		},

		notify: async (picked_packages_no: number) => {
			runInAction(async () => {
				try {
					this.pickupEnabled = false;

					const response = await this.rootContext.apiService.api(
						"/vendor/pickups/notify",
						{
							picked_packages_no,
						}
					);

					if (response.data) {
						const data = response.data.data.s2sNotifyPickedPackages;

						this.PickupsOperations.compareDate(data.timestamp_iso);
					} else {
						throw response.data.errors;
					}
				} catch (error) {
					this.pickupEnabled = false;

					TXLogger.error(
						"PickupsOperations.notify()",
						"failed to send notification",
						{
							error,
							extra: { picked_packages_no, vendorDetails: this.vendorDetails },
						}
					);
				}
			});
		},
	};
}
